import { styled, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  DataGridPro as MuiDataGrid,
  type DataGridProProps,
  type GridColDef,
  type GridEventListener,
  type GridRowParams,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { cloneElement, useCallback, useMemo, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PER_PAGE } from '~/lib/pagination';

export * from './pagination';

export const ROW_HEIGHT = 45;

export const ROW_SPACING = 0;

export const ROW_TOTAL_HEIGHT = ROW_HEIGHT + ROW_SPACING;

/**
 * Names a column whose header shows nothing (a thumbnail, status icons, row actions). The name is
 * visually hidden, so the grid looks the same but screen readers announce the column (WCAG 1.3.1).
 */
export const hiddenHeader = (
  headerName: string,
): Pick<GridColDef, 'headerName' | 'renderHeader'> => ({
  headerName,
  renderHeader: () => <span style={visuallyHidden}>{headerName}</span>,
});

/**
 * An actions cell has role=menu, so each action it renders is cloned with the menuitem role
 * its children must have (WCAG 1.3.1, 4.1.2). Our action components also read the role as the
 * cue to render their button bare, since the cell moves focus between its direct DOM children.
 * An actions column with no header of its own is named `actionsLabel`, visually hidden.
 */
export const withMenuItemActions = <T extends GridValidRowModel>(
  columns: readonly GridColDef<T>[],
  actionsLabel?: string,
): GridColDef<T>[] =>
  columns.map((column) =>
    'getActions' in column
      ? {
          ...column,
          ...(actionsLabel && !column.headerName && !column.renderHeader
            ? hiddenHeader(actionsLabel)
            : {}),
          getActions: (params: GridRowParams<T>) =>
            column
              .getActions(params)
              .map((action) => cloneElement(action, { role: 'menuitem' as const })),
        }
      : column,
  );

export const useMenuItemActions = <T extends GridValidRowModel>(
  columns: readonly GridColDef<T>[],
) => {
  const { t } = useTranslation();
  const actionsLabel = t('common.labels.actions');
  return useMemo(() => withMenuItemActions(columns, actionsLabel), [actionsLabel, columns]);
};

/**
 * Enter on a focused row checkbox toggles it, as the theme does for every other checkbox (the
 * grid's own key handler overrides that default). Returns whether it did. The header's select-all
 * checkbox stays on Space: the grid implements that key itself in a handler this cannot reach.
 */
const toggleCheckboxOnEnter = (event: KeyboardEvent): boolean => {
  const { target } = event;
  if (event.key !== 'Enter' || !(target instanceof HTMLInputElement) || target.type !== 'checkbox')
    return false;
  event.preventDefault();
  target.click();
  return true;
};

/**
 * Enter on a focused cell opens the row as a click anywhere on it does, so the keyboard has the
 * same path into an item (WCAG 2.1.1). A control focused inside the cell keeps Enter for itself,
 * and an editable cell keeps it for editing.
 */
const useRowEnterKey = (
  onRowClick: GridEventListener<'rowClick'> | undefined,
  onCellKeyDown: GridEventListener<'cellKeyDown'> | undefined,
): GridEventListener<'cellKeyDown'> =>
  useCallback(
    (params, event, details) => {
      onCellKeyDown?.(params, event, details);
      if (event.key !== 'Enter' || event.defaultPrevented || toggleCheckboxOnEnter(event)) return;
      if (!onRowClick || event.target !== event.currentTarget || params.isEditable) return;
      event.preventDefault();
      event.currentTarget.click();
    },
    [onCellKeyDown, onRowClick],
  );

const StyledManageDataGrid = styled(MuiDataGrid)(() => ({
  border: 0,
  '& .MuiDataGrid-row:hover': {
    background: 'white',
  },
  '& .MuiDataGrid-actionsCell': {
    gridGap: 0,
  },
  '& .MuiDataGrid-scrollbar': {
    display: 'none',
  },
})) as typeof MuiDataGrid;

export const ManageDataGrid = <T extends GridValidRowModel>({
  columns,
  onCellKeyDown,
  onRowClick,
  ...props
}: DataGridProProps<T>) => {
  const { t } = useTranslation();
  const menuItemColumns = useMenuItemActions(columns);
  const handleCellKeyDown = useRowEnterKey(onRowClick, onCellKeyDown);

  return (
    <StyledManageDataGrid<T>
      localeText={{
        MuiTablePagination: {
          labelRowsPerPage: t('common.pagination.per_page'),
        },
        footerTotalRows: t('common.pagination.total_rows'),
      }}
      {...props}
      columns={menuItemColumns}
      onCellKeyDown={handleCellKeyDown}
      onRowClick={onRowClick}
    />
  );
};

export const SeparatedDataGrid = <T extends GridValidRowModel>({
  columns,
  onCellKeyDown,
  onRowClick,
  sx,
  ...props
}: DataGridProProps<T>) => {
  const { t } = useTranslation();
  const menuItemColumns = useMenuItemActions(columns);
  const handleCellKeyDown = useRowEnterKey(onRowClick, onCellKeyDown);

  return (
    <MuiDataGrid<T>
      columns={menuItemColumns}
      onCellKeyDown={handleCellKeyDown}
      onRowClick={onRowClick}
      sx={(theme) => ({
        '--DataGrid-overlayHeight': '225px',
        '--DataGrid-rowBorderColor': theme.palette.borders.light,
        border: 0,
        fontSize: '14px',
        '& .MuiDataGrid-columnHeader': {
          color: theme.palette.text.secondary,
          fontSize: '0.7rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnSeparator--resizable': {
            color: 'rgba(0,0,0,0.25)',
            display: 'flex',
            '&:hover': {
              color: 'rgba(0,0,0,0.25)',
            },
          },
        },
        '& .MuiDataGrid-actionsCell': {
          gridGap: 0,
        },
        '& .MuiDataGrid-row': {
          borderRadius: '0',
          boxShadow: 'none',
          background: 'white',
          borderBottom: 1,
          borderBottomColor: 'borders.main',
          '&:hover': {
            background: theme.palette.table.hoverBackgroundColor,
            boxShadow: 'none',
            cursor: 'pointer',
          },
          '&.Mui-selected': {
            background: 'white',
            boxShadow: `inset 0 0 0 2px ${theme.palette.table.checkedBorderColor}, rgba(0, 0, 0, 0.1) 0px 1px 2px 0px`,
            '&:hover': {
              background: 'white',
              boxShadow: `inset 0 0 0 2px ${theme.palette.table.checkedBorderColor}, rgba(0, 0, 0, 0.1) 0px 4px 8px 0px`,
            },
          },
          '& .MuiDataGrid-cellCheckbox': {
            '& .Mui-disabled': {
              // "hide" disabled checkboxes
              visibility: 'hidden',
            },
          },
        },
        '& .MuiDataGrid-scrollbar': {
          display: 'none',
        },
        '& .MuiDataGrid-cell': {
          border: 0,
        },
        '& .readonly': {
          '&:hover': {
            cursor: 'default',
          },
        },
        ...sx,
      })}
      autoHeight
      checkboxSelection={false}
      disableColumnMenu
      disableColumnReorder
      hideFooterSelectedRowCount
      initialState={{
        pagination: {
          paginationModel: { pageSize: PER_PAGE, page: 0 },
        },
      }}
      localeText={{
        MuiTablePagination: {
          labelRowsPerPage: t('common.pagination.per_page'),
        },
        footerTotalRows: t('common.pagination.total_rows'),
      }}
      getRowHeight={() => ROW_HEIGHT}
      getRowSpacing={() => ({ top: ROW_SPACING })}
      pagination
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
        },
      }}
      {...props}
    />
  );
};

export const ReadOnlyDataGrid = <T extends GridValidRowModel>(props: DataGridProProps<T>) => (
  <SeparatedDataGrid<T> disableRowSelectionOnClick getRowClassName={() => 'readonly'} {...props} />
);

export const ScrollableDataGrid = <T extends GridValidRowModel>({
  enableHorizontalScrolling = false,
  hideFooter = true,
  pagination = false,
  sx,
  ...props
}: DataGridProProps<T> & { enableHorizontalScrolling?: boolean }) => {
  const theme = useTheme();

  return (
    <SeparatedDataGrid<T>
      autoHeight={false}
      disableVirtualization
      hideFooter={hideFooter}
      // TODO: eventually remove this
      // everywhere we are using this component should be handling pagination themselves with the shared
      // ListPagination component. setting pagination to true without passing through a pagination model
      // means that the data grid defaults to page size 50. that means 25 and 50 will appear to work, and
      // 100 will only show the first 50 and offer to move to the second page. you however cannot see that
      // it thinks there are 2 pages because we are hiding the footer as well.
      pagination={pagination}
      sx={{
        '& .MuiDataGrid-scrollbar--vertical': {
          display: 'block !important',
          top: 40,
          height: 'calc(100% - 40px)',
        },
        '& .MuiDataGrid-scrollbar--horizontal': {
          display: enableHorizontalScrolling ? 'block !important' : 'none !important',
        },
        '& .MuiDataGrid-filler': {
          display: 'none',
        },
        '& .MuiDataGrid-columnHeader--withLeftBorder': {
          borderLeftWidth: 0,
        },
        '& .MuiDataGrid-cell--pinnedRight': {
          background: theme.palette.table.backgroundColor,
          borderBottom: `1px solid ${theme.palette.table.borderColor}`,
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          '& .MuiDataGrid-row:hover': {
            '& .MuiDataGrid-cell--pinnedRight': {
              background: theme.palette.table.hoverBackgroundColor,
            },
          },
          '& .MuiDataGrid-row.Mui-selected': {
            '& .MuiDataGrid-cell--pinnedRight': {
              background: theme.palette.table.checkedBackgroundColor,
              boxShadow: `${theme.palette.table.checkedBorderColor} 0px 2px 0px 0px inset, ${theme.palette.table.checkedBorderColor} -2px 0px 0px 0px inset, ${theme.palette.table.checkedBorderColor} 0px -2px 0px 0px inset`,
            },
            '&:hover': {
              '& .MuiDataGrid-cell--pinnedRight': {
                background: theme.palette.table.checkedBackgroundColor,
              },
            },
          },
        },
        height: '100%',
        ...sx,
      }}
      {...props}
    />
  );
};
